import { useEffect, useState } from 'react';
import ButtonComponent from '../../components/button/button';
import { useNavigate } from 'react-router-dom';
import { Dayjs } from 'dayjs';
import { getSearchResults } from '../../features/search/service/search-service';
import DatePickerComponent from '../../components/input/input-date';
import { ExportDelayData } from './mapper/delay-mapper';
import { getMe } from '../../features/search/service/user-service';
import { getRoleById } from '../../features/search/service/role-service';
import { getCompaniesListSelect } from '../../features/search/service/company-service';
import { User } from '../../shared/types/user';
import { Role } from '../../shared/types/role';
import { SelectOption } from '../../shared/types/select-options';
import SelectComponent from '../../components/input/select';

const Reports = () => {
    const navigate = useNavigate();
    const [initialDate, setInitialDate] = useState<Dayjs | null>(null);
    const [finalDate, setFinalDate] = useState<Dayjs | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [userRole, setUserRole] = useState<Role | null>(null);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [loadingData, setLoadingData] = useState(true);

    const isSuperAdmin = userRole?.name === 'super-admin';

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoadingData(true);
                
                const userData = await getMe();
                setUser(userData);

                // Buscar a role do usuário
                let roleData: Role | null = null;
                if (userData.role_id) {
                    roleData = await getRoleById(userData.role_id);
                    setUserRole(roleData);
                }

                // Se for super-admin, buscar todas as empresas
                if (roleData?.name === 'super-admin') {
                    const companiesData = await getCompaniesListSelect();
                    setCompanies(companiesData);
                    
                    // Selecionar a primeira empresa por padrão
                    if (companiesData.length > 0) {
                        setSelectedCompanyId(String(companiesData[0].id));
                    }
                } else {
                    // Se não for super-admin, usar a empresa do usuário
                    const companyId = userData.company?.id || userData.companyId;
                    if (companyId) {
                        setSelectedCompanyId(companyId);
                    }
                }

            } catch (error) {
                console.error('❌ Erro ao buscar dados:', error);
                alert('Erro ao carregar informações. Por favor, faça login novamente.');
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, []);

    const handleDownloadReport = async () => {
        if (!selectedCompanyId) {
            console.error('❌ Nenhuma empresa selecionada');
            alert('Selecione uma empresa para gerar o relatório.');
            return;
        }

        try {
            setIsLoading(true);
            
            const queryParams = new URLSearchParams();
            queryParams.append('companyId', selectedCompanyId);
            
            if (initialDate) {
                const formattedStart = initialDate.format('YYYY-MM-DD');
                queryParams.append('startDate', formattedStart);
            }
            
            if (finalDate) {
                const formattedEnd = finalDate.format('YYYY-MM-DD');
                queryParams.append('endDate', formattedEnd);
            }
            
            const response = await getSearchResults(queryParams);
            
            if (response.data && response.data.length > 0) {
                // Buscar nome da empresa para o arquivo
                let companyName = 'empresa';
                
                if (isSuperAdmin) {
                    const selectedCompany = companies.find(c => String(c.id) === selectedCompanyId);
                    companyName = selectedCompany?.label || 'empresa';
                } else {
                    companyName = user?.company?.name || 'empresa';
                }
                
                const safeCompanyName = companyName.replace(/\s+/g, '_').toLowerCase();
                const fileName = `relatorio_delay_discounting_${safeCompanyName}`;
                
                ExportDelayData(response, fileName);
                
                alert(`✅ Relatório gerado com sucesso!\n${response.data.length} registro(s) encontrado(s).`);
            } else {
                alert('Nenhum registro encontrado para o período selecionado.\n\nVerifique:\n- Se existem dados cadastrados para a empresa\n- Se o período selecionado está correto');
            }
        } catch (error) {
            alert('Erro ao gerar o relatório. Por favor, tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    if (loadingData) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <p style={{ color: '#3a8d8e', fontFamily: 'Roboto, sans-serif' }}>Carregando...</p>
            </div>
        );
    }
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            <img src="../../../assets/logo-inteira.png" alt="Logo delay discounting" className="logo" style={{ width: '300px', height: '200px' }}/>
            {isSuperAdmin && (
                <h1 
                style={{
                    textAlign: 'center', 
                    fontSize: '2rem', 
                    fontFamily: 'Roboto, sans-serif', 
                    color: '#3a8d8e',
                    margin: '1rem 0',
                }}>
                Selecione a empresa e o período
            </h1>
            )}

            {/* Select de empresa - só aparece para super-admin */}
            {isSuperAdmin && companies.length > 0 && (
                <div style={{ width: '300px', margin: '1rem 0' }}>
                    <SelectComponent
                        label="Empresa"
                        value={selectedCompanyId}
                        onChange={(value) => setSelectedCompanyId(String(value))}
                        options={companies}
                    />
                </div>
            )}

            <h2 style={{ textAlign: 'center', fontSize: '1.5rem', fontFamily: 'Roboto, sans-serif', color: '#3a8d8e', margin: '1rem 0' }}>
                Selecione o período
            </h2>

            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', margin: '1rem' }}>
                <DatePickerComponent 
                    label="Data Inicial"
                    value={initialDate}
                    onChange={(event: Dayjs | null) => setInitialDate(event)}
                    styles={{
                        margin: '0 1rem',
                    }}
                />
                
                <DatePickerComponent 
                    label="Data Final"
                    value={finalDate}
                    onChange={(event: Dayjs | null) => setFinalDate(event)}
                />
            </div>

            <ButtonComponent 
                label={isLoading ? "Gerando..." : "Baixar Relatório"} 
                onClick={() => handleDownloadReport()}
                props={{
                    disabled: !initialDate || isLoading || !selectedCompanyId,
                }}
            />
            <ButtonComponent 
                isReturnButton={true} 
                label="Voltar ao início" 
                onClick={() => navigate('/home')} 
                props={{
                    disabled: isLoading,
                }}
            />
        </div>
    );
}

export default Reports;